import React from 'react';
import { Menu } from '@base-ui-components/react/menu';
import Button from './Button';
import { useModalContainer } from './Modal';

// DropdownButton + MenuItem wrap Base UI Menu, keeping react-bootstrap's API
// (title / pullRight / onSelect / MenuItem eventKey|divider|header) so call
// sites migrate with an import swap. Base UI owns keyboard nav, typeahead,
// outside-click and focus management.

export const MenuSelectContext = React.createContext(undefined);

/**
 * @param {object} props
 * @param {React.ReactNode} props.title Trigger button label.
 * @param {string} [props.id] Forwarded to the trigger button.
 * @param {'blue'|'white'|'red'|'link'} [props.variant] Trigger Button variant.
 * @param {'large'|'small'|'xsmall'} [props.size] Trigger Button size.
 * @param {boolean} [props.pullRight] Right-align the menu to the trigger.
 * @param {boolean} [props.dropup] Open the menu above the trigger.
 * @param {boolean} [props.disabled] Disable the trigger.
 * @param {boolean} [props.noCaret] Hide the caret glyph.
 * @param {(eventKey: any, event: Event) => void} [props.onSelect] Fires for
 *   any child MenuItem without its own onSelect.
 * @param {string} [props.menuClassName] Extra class on the menu popup.
 * @param {(open: boolean, eventDetails: object) => void} [props.onToggle]
 *   Fires whenever the menu opens or closes, for any reason (trigger click,
 *   item selection, outside click, Escape, focus-out). Use this, not the
 *   trigger's onClick/onBlur, to mirror the menu's open state: Base UI moves
 *   focus into the popup on open, so the trigger never holds focus during
 *   the click and its blur is not a reliable close signal.
 * @param {false} [props.finalFocus] Pass false when a menu item hands focus to
 *   an element of its own (e.g. an inline rename input): it disables the
 *   menu's close-time return-to-trigger focus (Base UI Menu.Popup finalFocus),
 *   which otherwise races that element's focus and can blur it a frame after
 *   it mounts. Escape-key closes still return focus to the trigger — this
 *   component restores it explicitly so keyboard dismissal keeps its place.
 *   Only `false` is supported. Base UI's other forms are traps here: the
 *   function form receives the interaction MODALITY ('mouse'|'keyboard'|...),
 *   not the close reason, so it cannot tell Escape from keyboard
 *   item-activation, and any non-boolean value disables Base UI's
 *   focus-moved-elsewhere guard, making the return-focus steal unconditional.
 *   Flipping the prop from item onClick state does not work either — the flip
 *   commits in the same React commit that unmounts the popup, too late for
 *   the popup to re-render with the new value.
 */
const DropdownButton = ({
  title,
  id,
  variant,
  size,
  pullRight,
  dropup,
  disabled,
  noCaret,
  onSelect,
  className,
  menuClassName,
  finalFocus,
  onToggle,
  children,
  ...rest
}) => {
  // Inside a modal Dialog the menu must portal into the dialog's popup, not
  // <body>, or the dialog's focus trap closes it on open (see Modal.jsx).
  const modalContainer = useModalContainer();

  const triggerRef = React.useRef(null);
  const handleOpenChange = React.useCallback((open, eventDetails) => {
    if (finalFocus === false && !open && eventDetails?.reason === 'escape-key') {
      triggerRef.current?.focus();
    }
    onToggle?.(open, eventDetails);
  }, [finalFocus, onToggle]);

  return (
    <Menu.Root modal={false} onOpenChange={handleOpenChange}>
      <Menu.Trigger
        disabled={disabled}
        render={
          <Button ref={triggerRef} variant={variant} size={size} className={className} id={id} {...rest}>
            {title}
            {!noCaret && <span className="ui-caret" aria-hidden="true" />}
          </Button>
        }
      />
      <Menu.Portal container={modalContainer}>
        <Menu.Positioner side={dropup ? 'top' : 'bottom'} align={pullRight ? 'end' : 'start'} sideOffset={2}>
          <Menu.Popup className={['ui-menu', menuClassName].filter(Boolean).join(' ')} finalFocus={finalFocus}>
            <MenuSelectContext.Provider value={onSelect}>{children}</MenuSelectContext.Provider>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
};

export default DropdownButton;
